import { useState, useCallback } from "react"

import User from "./user"
import { LoginData, LoginResponse, IUser } from "./types"
import { dispatchLoggedInEvent, dispatchLoggedOutEvent } from "."
import { AxiosError } from "axios"
import { useLocation, useNavigate } from "react-router-dom"
import api from "../../services/api"
import { MutationOptions, useMutation, useQuery } from "@tanstack/react-query"

export function useCurrentUser() {
  const navigate = useNavigate()
  const location = useLocation()
  const [enabled, setEnabled] = useState<boolean>(
    () => !!localStorage.getItem("is-authenticated")
  )
  const { data, isInitialLoading } = useQuery<IUser>(
    ["account", "profile", { include: ["permissions", "roles", "accesses"] }],
    {
      refetchOnWindowFocus: enabled,
      retry: false,
      enabled,
      select: useCallback((data: any) => new User(data), []),
      onSuccess() {
        if (location.pathname === "/maintenance") {
          navigate("/")
        }
      },
      onError(error: any) {
        // token expired
        if (error.response.status === 401) {
          dispatchLoggedOutEvent()

          setEnabled(false)
        }

        if (error.response.status === 409) {
          navigate("/auth/active-profile")
        }
        if (error.response.status === 503) {
          navigate("/maintenance")
        }
      },
    }
  )
  return {
    data,
    isInitialLoading,
    enable: useCallback(() => setEnabled(true), []),
    disable: useCallback(() => setEnabled(false), []),
  }
}

export function useLogin(
  config?: MutationOptions<LoginResponse, AxiosError, LoginData>
) {
  return useMutation<LoginResponse, AxiosError, LoginData>(
    (data: LoginData) => api.post("account/login", data),
    {
      onSuccess: () => {
        dispatchLoggedInEvent()
      },
      meta: { handleError: false },
      ...config,
    }
  )
}

export function useLogout() {
  return () => {
    api.post("account/logout")
    dispatchLoggedOutEvent()
  }
}

export function useCreateAccount(
  config?: MutationOptions<any, AxiosError, { email: string }>
) {
  return useMutation<any, AxiosError, { email: string }>(
    (data) => api.post("portal/signup", data),
    config
  )
}
